/**
 * Hook personalizado para conexión WebSocket de supervisión
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useSupervisionWebSocket = (sessionId, token, mode = 'instructor') => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [vitals, setVitals] = useState(null);
  const [supervisionMode, setSupervisionMode] = useState('observador');
  const [hasControl, setHasControl] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Construir URL del WebSocket
  const getWebSocketUrl = useCallback(() => {
    if (!sessionId || !token) return null;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    return `${wsProtocol}//${wsHost}/ws/simulation/${sessionId}?token=${token}&mode=${mode}`;
  }, [sessionId, token, mode]);

  // Enviar mensaje por WebSocket
  const sendMessage = useCallback((type, data = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...data }));
      return true;
    }
    return false;
  }, []);

  // Enviar mensaje de chat
  const sendChatMessage = useCallback((content, resourceUrl = null) => {
    return sendMessage('chat', { content, resource_url: resourceUrl });
  }, [sendMessage]);

  // Enviar pista al estudiante
  const sendHint = useCallback((content, hintType = 'sugerencia') => {
    return sendMessage('hint', { content, hint_type: hintType });
  }, [sendMessage]);

  // Tomar control de la simulación
  const takeControl = useCallback(() => {
    return sendMessage('take_control');
  }, [sendMessage]);

  // Liberar control
  const releaseControl = useCallback(() => {
    return sendMessage('release_control');
  }, [sendMessage]);

  // Cambiar modo de supervisión
  const setMode = useCallback((newMode) => {
    return sendMessage('set_mode', { mode: newMode });
  }, [sendMessage]);

  // Obtener información de sesión
  const requestSessionInfo = useCallback(() => {
    return sendMessage('get_session_info');
  }, [sendMessage]);

  // Conectar WebSocket
  const connect = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) return;

    // Limpiar conexión anterior
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket conectado');
      setIsConnected(true);
      setConnectionError(null);
      
      // Iniciar heartbeat
      heartbeatIntervalRef.current = setInterval(() => {
        sendMessage('heartbeat');
      }, 30000);
    };

    ws.onclose = (event) => {
      console.log('WebSocket desconectado', event.code, event.reason);
      setIsConnected(false);
      
      // Limpiar heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      
      // Reconectar si no fue cierre intencional
      if (event.code !== 1000 && event.code !== 4001) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Intentando reconectar...');
          connect();
        }, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionError('Error de conexión');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
        
        // Procesar según tipo de evento
        switch (data.event_type) {
          case 'connected':
            setSessionInfo(data.session_info);
            break;
          
          case 'session_info':
            setSessionInfo(data);
            break;
          
          case 'vitals_update':
            setVitals(data.vitals);
            break;
          
          case 'chat_message':
            setMessages(prev => [...prev, data]);
            break;
          
          case 'instructor_hint':
            setMessages(prev => [...prev, { ...data, isHint: true }]);
            break;
          
          case 'mode_change':
            if (data.instructor_id === getCurrentUserId()) {
              setSupervisionMode(data.new_mode);
              setHasControl(data.has_control);
            }
            break;
          
          case 'control_response':
            if (data.success) {
              setHasControl(data.action === 'take_control');
              if (data.action === 'take_control') {
                setSupervisionMode('control_total');
              }
            }
            break;
          
          case 'mode_response':
            if (data.success) {
              setSupervisionMode(data.mode);
              setHasControl(data.mode === 'control_total');
            }
            break;
          
          case 'user_joined':
          case 'user_left':
            // Actualizar información de sesión
            requestSessionInfo();
            break;
          
          default:
            // Otros eventos
            break;
        }
      } catch (e) {
        console.error('Error parseando mensaje:', e);
      }
    };
  }, [getWebSocketUrl, sendMessage, requestSessionInfo]);

  // Desconectar
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Cierre intencional');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Helper para obtener user ID del token
  const getCurrentUserId = () => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch {
      return null;
    }
  };

  // Conectar al montar
  useEffect(() => {
    if (sessionId && token) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [sessionId, token, connect, disconnect]);

  return {
    isConnected,
    connectionError,
    lastMessage,
    sessionInfo,
    messages,
    vitals,
    supervisionMode,
    hasControl,
    sendMessage,
    sendChatMessage,
    sendHint,
    takeControl,
    releaseControl,
    setMode,
    requestSessionInfo,
    connect,
    disconnect
  };
};

export default useSupervisionWebSocket;
